import { useEffect, useState } from "react";
import { motion } from "motion/react";
import {
    StacyBottomBun,
    StacyMeatCheeseLayer,
    StacySauceLayer,
    StacyTopBun,
} from "./StacyBurger";

import logoImg from "../assets/marca/logo-red.svg";

interface SplashStacyProps {
    isLeaving: boolean;
    onStartExit: () => void;
    onComplete: () => void;
}

type Phase = "enter" | "hold" | "handoff" | "revealLogo" | "fadeout";

const COLORS = {
    blue: "#113F7F",
    red: "#BA4643",
    beige: "#F2F1DE",
};

function BurgerRealStack({ phase }: { phase: Phase }) {
    const enterEase = [0.22, 1, 0.36, 1] as const;
    const exitEase = [0.76, 0, 0.24, 1] as const;

    const isStackLeaving =
        phase === "handoff" || phase === "revealLogo" || phase === "fadeout";

    const layers = [
        {
            id: "bottom-bun",
            top: 210,
            enterDelay: 0,
            exitDelay: 0,
            enterRotate: -4,
            exitRotate: 4,
            node: <StacyBottomBun stroke={COLORS.beige} />,
        },
        {
            id: "lower-sauce",
            top: 192,
            enterDelay: 0.08,
            exitDelay: 0.06,
            enterRotate: 4,
            exitRotate: -4,
            node: (
                <div className="-scale-y-100">
                    <StacySauceLayer stroke={COLORS.beige} outline={COLORS.beige} />
                </div>
            ),
        },
        {
            id: "meat-cheese",
            top: 164,
            enterDelay: 0.16,
            exitDelay: 0.12,
            enterRotate: -5,
            exitRotate: 3,
            node: (
                <StacyMeatCheeseLayer
                    stroke={COLORS.beige}
                    outline={COLORS.beige}
                />
            ),
        },
        {
            id: "upper-sauce",
            top: 145,
            enterDelay: 0.24,
            exitDelay: 0.18,
            enterRotate: 5,
            exitRotate: -3,
            node: <StacySauceLayer stroke={COLORS.beige} outline={COLORS.beige} />,
        },
        {
            id: "top-bun",
            top: 92,
            enterDelay: 0.32,
            exitDelay: 0.24,
            enterRotate: -6,
            exitRotate: 5,
            node: <StacyTopBun stroke={COLORS.beige} />,
        },
    ];

    return (
        <motion.div
            className="relative h-[320px] w-[270px] sm:h-[350px] sm:w-[300px] md:h-[370px] md:w-[320px]"
            animate={{
                scale:
                    phase === "enter"
                        ? 1.08
                        : phase === "hold"
                            ? 1.1
                            : phase === "handoff"
                                ? 1.04
                                : phase === "revealLogo"
                                    ? 1.02
                                    : 1,
            }}
            transition={{
                duration: 0.55,
                ease: enterEase,
            }}
        >
            {layers.map((layer) => (
                <motion.div
                    key={layer.id}
                    className="absolute left-1/2 -translate-x-1/2"
                    style={{ top: layer.top }}
                    initial={{
                        y: "-145vh",
                        rotate: layer.enterRotate,
                        opacity: 1,
                    }}
                    animate={
                        isStackLeaving
                            ? {
                                y: "130vh",
                                rotate: layer.exitRotate,
                                opacity: 1,
                            }
                            : {
                                y: 0,
                                rotate: 0,
                                opacity: 1,
                            }
                    }
                    transition={{
                        duration: isStackLeaving ? 0.74 : 0.82,
                        delay: isStackLeaving ? layer.exitDelay : layer.enterDelay,
                        ease: isStackLeaving ? exitEase : enterEase,
                    }}
                >
                    {layer.node}
                </motion.div>
            ))}
        </motion.div>
    );
}

export default function SplashStacy({
    isLeaving,
    onStartExit,
    onComplete,
}: SplashStacyProps) {
    const [phase, setPhase] = useState<Phase>("enter");

    useEffect(() => {
        const t1 = setTimeout(() => setPhase("hold"), 1150);
        const t2 = setTimeout(() => setPhase("handoff"), 1900);
        const t3 = setTimeout(() => setPhase("revealLogo"), 2550);

        // Acá arranca el handoff real con el home
        const t4 = setTimeout(() => {
            onStartExit();
        }, 3150);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            clearTimeout(t4);
        };
    }, [onStartExit]);

    useEffect(() => {
        if (!isLeaving) return;

        setPhase("fadeout");

        const doneTimer = setTimeout(() => {
            onComplete();
        }, 980);

        return () => clearTimeout(doneTimer);
    }, [isLeaving, onComplete]);

    const panelIsLeaving =
        phase === "handoff" || phase === "revealLogo" || phase === "fadeout";

    return (
        <motion.div
            className="fixed inset-0 z-[100] overflow-hidden pointer-events-none"
            animate={{
                opacity: isLeaving ? 0 : 1,
                scale: isLeaving ? 1.01 : 1,
            }}
            transition={{
                duration: 0.95,
                ease: [0.22, 1, 0.36, 1],
            }}
        >
            <div className="absolute inset-0 bg-[#F2F1DE]" />

            <div className="absolute inset-0 flex items-center justify-center">
                <motion.img
                    src={logoImg}
                    alt="Stacy"
                    className="h-auto w-[118px] sm:w-[150px] md:w-[178px]"
                    initial={{ opacity: 0, scale: 0.9, y: 8 }}
                    animate={{
                        opacity:
                            phase === "handoff"
                                ? 0.35
                                : phase === "revealLogo" || phase === "fadeout"
                                    ? 1
                                    : 0,
                        scale:
                            phase === "handoff"
                                ? 0.96
                                : phase === "revealLogo"
                                    ? 1
                                    : phase === "fadeout"
                                        ? 1.03
                                        : 0.9,
                        y: phase === "fadeout" ? -8 : 0,
                    }}
                    transition={{
                        duration: 0.5,
                        ease: [0.22, 1, 0.36, 1],
                    }}
                />
            </div>

            <motion.div
                className="absolute inset-0"
                animate={{
                    backgroundColor:
                        phase === "handoff" || phase === "revealLogo" || phase === "fadeout"
                            ? COLORS.red
                            : COLORS.blue,
                    y: panelIsLeaving ? "108%" : "0%",
                }}
                transition={{
                    backgroundColor: {
                        duration: 0.42,
                        ease: [0.22, 1, 0.36, 1],
                    },
                    y: {
                        duration: 0.95,
                        delay: phase === "handoff" ? 0.08 : 0,
                        ease: [0.76, 0, 0.24, 1],
                    },
                }}
            >
                <motion.div
                    className="absolute inset-0"
                    animate={{
                        opacity: phase === "handoff" ? 0.15 : 0.08,
                    }}
                    transition={{ duration: 0.4 }}
                >
                    <div className="absolute left-[-10%] top-[-15%] h-[42vw] w-[42vw] rounded-full bg-white blur-[150px]" />
                    <div className="absolute bottom-[-18%] right-[-8%] h-[36vw] w-[36vw] rounded-full bg-white blur-[150px]" />
                </motion.div>

                <div className="relative flex h-full w-full items-center justify-center px-6">
                    <motion.div
                        animate={{
                            y:
                                phase === "handoff"
                                    ? 96
                                    : phase === "revealLogo"
                                        ? 118
                                        : phase === "fadeout"
                                            ? 126
                                            : 0,
                        }}
                        transition={{
                            duration: 0.82,
                            ease: [0.76, 0, 0.24, 1],
                        }}
                    >
                        <BurgerRealStack phase={phase} />
                    </motion.div>
                </div>
            </motion.div>
        </motion.div>
    );
}